import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Form, Input, Select, DatePicker, Row, Col, Divider } from 'antd';
import { addAsset, updateAsset } from '@/api/assets';
import type { Asset, AssetCategory } from '@/types/resources/asset';
import { AssetStatus } from '@/types/resources/asset';
import { useAuth } from '@/contexts/AuthContext';
import dayjs, { Dayjs } from 'dayjs';
import assetsListJson from '@/utils/assetsList.json';
import { BlockFloorSelector } from '@/components/common/BlockFloorSelector';
import { AmenitySelector } from '@/components/common/AmenitySelector';
import CurrencyInput from '@/components/common/CurrencyInput';
import type { Money } from '@/types/common';

interface AssetModalProps {
  open: boolean;
  asset: Asset | null;
  onSuccess: () => void;
  onCancel: () => void;
  category: string;
}

interface AssetFormValues extends Omit<Partial<Asset>, 'purchaseDate' | 'purchasePrice' | 'warranty'> {
  purchaseDate?: Dayjs;
  purchasePrice?: Money;
  warranty?: {
    startDate?: Dayjs;
    endDate?: Dayjs;
    details?: string;
    provider?: string;
  };
  blockId?: string;
  floorSlug?: string;
  amenityId?: string;
}

export const AssetModal: React.FC<AssetModalProps> = ({
  open,
  asset,
  onSuccess,
  onCancel,
  category,
}) => {
  const { t } = useTranslation(['assets', 'common']);
  const queryClient = useQueryClient();
  const { firestoreUser } = useAuth();
  const [form] = Form.useForm<AssetFormValues>();
  
  // State to store selected data from selectors
  const [selectedBlockData, setSelectedBlockData] = useState<{ id: string; name: string } | null>(null);
  const [selectedFloorData, setSelectedFloorData] = useState<{ name: string; slug: string } | null>(null);
  const [selectedAmenityData, setSelectedAmenityData] = useState<{ id: string; name: string } | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<AssetCategory>(category as AssetCategory);

  // Mutations
  const addMutation = useMutation({
    mutationFn: (data: Omit<Asset, 'id'>) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return addAsset(data, firestoreUser);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      onSuccess();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Asset> }) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return updateAsset(id, data, firestoreUser);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      onSuccess();
    },
  });

  // Reset form when modal opens/closes or asset changes
  useEffect(() => {
    if (open) {
      if (asset) {
        form.setFieldsValue({
          ...asset,
          purchaseDate: asset.purchaseDate ? dayjs(asset.purchaseDate) : undefined,
          purchasePrice: asset.purchasePrice,
          warranty: asset.warranty ? {
            startDate: asset.warranty.startDate ? dayjs(asset.warranty.startDate) : undefined,
            endDate: asset.warranty.endDate ? dayjs(asset.warranty.endDate) : undefined,
            details: asset.warranty.details,
            provider: asset.warranty.provider,
          } : undefined,
          blockId: asset.block?.id,
          floorSlug: asset.floor?.slug,
          amenityId: asset.amenity?.id,
        });
        
        // Set selected data for existing asset
        if (asset.block) {
          setSelectedBlockData({ id: asset.block.id, name: asset.block.name });
        }
        if (asset.floor) {
          setSelectedFloorData({ name: asset.floor.name, slug: asset.floor.slug });
        }
        if (asset.amenity) {
          setSelectedAmenityData({ id: asset.amenity.id, name: asset.amenity.name });
        }
        if (asset.category) {
          setSelectedCategory(asset.category);
        }
      } else {
        form.resetFields();
        form.setFieldsValue({ category: category as AssetCategory });
        setSelectedCategory(category as AssetCategory);
        
        // Reset selected data for new asset
        setSelectedBlockData(null);
        setSelectedFloorData(null);
        setSelectedAmenityData(null);
      }
    }
  }, [open, asset, form, category]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const dataToSave = {
        ...values,
        purchaseDate: values.purchaseDate?.toDate(),
        purchasePrice: values.purchasePrice,
        warranty: values.warranty && values.warranty.startDate && values.warranty.endDate && values.warranty.details && values.warranty.provider ? {
          startDate: values.warranty.startDate.toDate(),
          endDate: values.warranty.endDate.toDate(),
          details: values.warranty.details,
          provider: values.warranty.provider,
        } : undefined,
        // Use selected data from selectors
        block: selectedBlockData || undefined,
        floor: selectedFloorData || undefined,
        amenity: selectedAmenityData || undefined,
        // Remove form-specific fields
        blockId: undefined,
        floorSlug: undefined,
        amenityId: undefined,
      };

      if (asset) {
        updateMutation.mutate({ id: asset.id, data: dataToSave });
      } else {
        addMutation.mutate(dataToSave as Omit<Asset, 'id'>);
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCategoryChange = (value: AssetCategory) => {
    setSelectedCategory(value);
    form.setFieldsValue({ 
      category: value,
      subCategory: undefined,
      name: ''
    });
  };

  const handleSubCategoryChange = (value: string) => {
    const selectedAsset = getSubCategoryOptions().find(option => option.value === value);
    if (selectedAsset) {
      form.setFieldsValue({ 
        subCategory: value,
        name: selectedAsset.label
      });
    }
  };

  const getCategoryOptions = () => {
    return assetsListJson.map(cat => ({
      label: t(`assets:categories.${cat.slug}`, cat.category),
      value: cat.slug as AssetCategory
    }));
  };

  const getSubCategoryOptions = () => {
    const currentCategory = assetsListJson.find(cat => cat.slug === selectedCategory);
    if (!currentCategory) return [];
    
    return currentCategory.assets.map(asset => ({
      label: asset.name,
      value: asset.slug
    }));
  };

  return (
    <Modal
      title={asset ? t('assets:actions.edit') : t('assets:actions.add')}
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={addMutation.isPending || updateMutation.isPending}
      okText={t('common:actions.save')}
      cancelText={t('common:actions.cancel')}
      width={800}
    >
      <Form
        form={form}
        layout="vertical"
        name="assetForm"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="category"
              label={t('assets:fields.category')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select 
                placeholder={t('assets:fields.category')}
                onChange={handleCategoryChange}
                options={getCategoryOptions()}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="subCategory"
              label={t('assets:fields.subCategory', 'Asset Type')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select 
                placeholder={t('assets:fields.selectAssetType', 'Select asset type')}
                onChange={handleSubCategoryChange}
                options={getSubCategoryOptions()}
                disabled={!selectedCategory}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label={t('assets:fields.name')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Input placeholder={t('assets:fields.namePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="status"
              label={t('assets:fields.status')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select placeholder={t('assets:fields.statusPlaceholder')}>
                {Object.values(AssetStatus).map(status => (
                  <Select.Option key={status} value={status}>
                    {t(`assets:status.${status}`, { defaultValue: status })}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name={['specifications', 'brand']}
              label={t('assets:fields.brand')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Input placeholder={t('assets:fields.brandPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={['specifications', 'model']}
              label={t('assets:fields.model')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Input placeholder={t('assets:fields.modelPlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="purchaseDate"
              label={t('assets:fields.purchaseDate')}
            >
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <CurrencyInput
              value={form.getFieldValue('purchasePrice')}
              onChange={(value) => form.setFieldValue('purchasePrice', value)}
              label={t('assets:fields.purchasePrice')}
            />
          </Col>
        </Row>

        {/* Block and Floor Selection */}
        <Divider>{t('assets:sections.location')}</Divider>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="location"
              label={t('assets:fields.location')}
            >
              <Input placeholder={t('assets:fields.locationPlaceholder')} />
            </Form.Item>
          </Col>
        </Row>
        <BlockFloorSelector
          onBlockDataChange={setSelectedBlockData}
          onFloorDataChange={setSelectedFloorData}
        />

        {/* Amenity Selection */}
        <AmenitySelector
          onAmenityDataChange={setSelectedAmenityData}
        />

        {/* Warranty Information */}
        <Divider>{t('assets:sections.warranty')}</Divider>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name={['warranty', 'startDate']}
              label={t('assets:fields.warrantyStartDate')}
            >
              <DatePicker style={{ width: '100%' }} format={t('assets:dateFormat')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={['warranty', 'endDate']}
              label={t('assets:fields.warrantyEndDate')}
            >
              <DatePicker style={{ width: '100%' }} format={t('assets:dateFormat')} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name={['warranty', 'provider']}
              label={t('assets:fields.warrantyProvider')}
            >
              <Input placeholder={t('assets:fields.warrantyProviderPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={['warranty', 'details']}
              label={t('assets:fields.warrantyDetails')}
            >
              <Input placeholder={t('assets:fields.warrantyDetailsPlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="notes"
          label={t('assets:fields.notes')}
        >
          <Input.TextArea rows={3} placeholder={t('assets:fields.notesPlaceholder')} />
        </Form.Item>
      </Form>
    </Modal>
  );
}; 